// lib/types/menu.ts — extracted from lib/types.ts
// Domain types for Menuraq SaaS platform (barrel re-export target).

import { ALLERGEN_LABELS, DIETARY_TAG_ENTRIES } from '@/lib/menu/allergen-vocabulary';
import type { Branch } from './business';

export interface Menu {
  id: string;
  business_id: string;
  branch_id?: string | null;
  content_menu_id?: string | null;
  title: string;
  description?: string;
  slug: string;
  currency?: string;
  template_id?: string;
  custom_template_id?: string;
  theme_id?: string;
  header_id?: string;
  slider?: boolean;
  status?: 'live' | 'draft' ;
  isPublished?: boolean;
  created_at?: string;
  updated_at?: string;
  menu_items?: MenuItem[];
  menu_collections?: MenuCollection[];
  branch?: Branch | null;
  view_count?: number;
  /** Doc 03 §4 — menu-level colour/font override layer applied last at render. */
  design_overrides?: import('@/lib/menu-themes/design-overrides').DesignOverrides;
  }

export interface MenuCollection {
  id: string;
  menu_id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  sort_order?: number;
  is_default?: boolean;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface MenuCategories {
  id: string;
  menu_id: string;
  collection_id?: string | null;
  name: string;
  description?: string;
  category_type?: 'food' | 'drink' | 'drinks' | 'other' | null;
  display_order?: number;
  is_active?: boolean;
  /** ADR-37 slice 3: category-level POS-visibility default — NULL (no override)
   *  means items inherit existing behavior. pos_visible is the only category
   *  default (product_behavior defaults were dropped in session-92 — lean scope,
   *  no runtime consumer). */
  default_pos_visible?: boolean | null;
  created_at?: string;
  updated_at?: string;
}

export interface MenuItem {
  id: string;
  menu_id: string;
  title: string;
  description?: string;
  price: number;
  // Per-type catalog spine (doc 38 Part 1). `kind` decides which editor renders
  // and how `price` is interpreted; `type_profile` holds kind-specific structure
  // (package tiers, drink ABV, …), validated by lib/menu/item-kinds. Optional
  // because older cached rows predate the columns; both resolve to dish defaults.
  kind?: import('@/lib/menu/item-kinds/types').ItemKind;
  pricing_mode?: import('@/lib/menu/item-kinds/types').PricingMode;
  type_profile?: import('@/lib/menu/item-kinds/types').ItemTypeProfile;
  // Weight pricing fields (doc 07 , weight/scale pricing)
  price_per_unit_weight?: number | null;
  weight_unit?: 'kg' | 'lb' | 'g' | null;
  min_weight?: number | null;
  max_weight?: number | null;
  // Per-plate pricing fields (buffet type)
  plate_price?: number | null;
  plate_weight_limit?: number | null;
  overweight_surcharge?: number | null;
  category: string;
  image?: string;
  imageFile?: File | null;
  // Cut-out columns (doc 62 §4.3, §9.2). The background-removal worker writes
  // here instead of overwriting `image`. The image column stays the photograph.
  image_cutout_url?: string | null;
  image_cutout_status?: 'none' | 'queued' | 'processing' | 'ready' | 'failed' | 'stale';
  image_cutout_source?: string | null;
  image_cutout_at?: string | null;
  allergens?: string[];
  /**
   * U6 (doc 66, doc 67 §3.10): merchant-authored pairing suggestions — ids of
   * other menu_items in the same business. The guest assistant's
   * `guest.pairings` tool prefers these over order-derived co-occurrence.
   * Optional because older cached rows predate the column.
   */
  pairing_items?: string[];
  /**
   * U6 (doc 66, doc 67 §3.10): estimated prep time in minutes. Null = not set.
   * The guest assistant cites this for "how long will it take".
   */
  preparation_time?: number | null;
  /** U6: 0=mild, 1=medium, 2=hot, 3=very hot, 4=extreme. Null = not set. */
  spice_level?: number | null;
  /** U6: small|regular|large|shareable. Null = not set. */
  portion_size?: string | null;
  /** U6: 0–100 popularity score. Null = not set. */
  popularity_score?: number | null;
  /** U6: merchant-authored free text (e.g. "comfort food, light, refreshing"). */
  semantic_description?: string | null;
  /** CAT-02 (doc 20 §5.2, R20.4): true once the merchant has explicitly confirmed allergen info. */
  allergens_declared?: boolean;
  dietary_tags?: string[];
  nutritional_info?: Record<string, unknown> | string;
  category_id?: string;
  display_order?: number;
  is_available?: boolean;
  is_featured?: boolean;
  is_coming_soon?: boolean;
  is_alcoholic?: boolean;
  tags?: string[];
  variants?: { [key: string]: string };
  available_Days?: string[]; // e.g. ['Monday', 'Wednesday']
  available_Times?: { start: string; end: string }; // e.g. { start: '11:00', end: '14:00' }
  available_from?: string | null;
  available_until?: string | null;
  sell_state?: MenuItemSellState;
  sell_state_reason?: string | null;
  stop_sell_until?: string | null;
  available_for_order?: boolean;
  availability_label?: string;
  cost_price?: number | null;
  /**
   * ADR-37 slice 1 (audit P1): stable POS/SKU identifier (price look-up code).
   * Null = no PLU yet. Business-scoped uniqueness lands with `catalog_products`
   * (slice 1b) — `menu_items` has no business_id to scope it to.
   */
  plu?: string | null;
  /** ADR-37 slice 1: optional barcode/EAN/UPC for scanner entry at the POS. */
  barcode?: string | null;
  /**
   * ADR-37 slice 1: kitchen/bar display name printed on tickets and KDS.
   * Falls back to `title` when null.
   */
  kitchen_name?: string | null;
  /**
   * ADR-37 slice 1: POS visibility, independent of `is_available`. `false` =
   * the guest menu may show it, the POS never lists it. Defaults to true.
   */
  pos_visible?: boolean;
  /**
   * ADR-37 slice 1: how the POS treats the line when adding it to an order.
   * Defaults to `normal`.
   */
  product_behavior?: MenuItemProductBehavior;
  /**
   * ADR-37 slice 1b: link to the business-scoped product master row
   * (`catalog_products`). Null = item is not placement-linked yet; identity
   * resolves from the item's own columns.
   */
  catalog_product_id?: string | null;
  option_groups?: Array<MenuItemOptionGroup & { options?: MenuItemOption[] }>;
  created_at?: string;
  updated_at?: string;
}

export type MenuItemSellState = 'available' | 'sold_out' | 'paused' | 'coming_soon';

/**
 * ADR-37 slice 1 (audit P1 "Add POS and kitchen product metadata"): how the
 * POS treats a line when it is added to an order. Cross-kind operational
 * identity — deliberately NOT part of `type_profile`.
 *
 * - `normal` — ordinary line, quantity defaults to 1.
 * - `quantity_prompt` — POS prompts for a quantity before adding.
 * - `open_price` — cashier types the price (market fish, deli by weight).
 * - `service_line` — a charge line, not a food item (corkage, cover charge).
 */
export type MenuItemProductBehavior = 'normal' | 'quantity_prompt' | 'open_price' | 'service_line';

/**
 * ADR-37 slice 1b: the business-scoped product identity master. One row per
 * reusable product; `menu_items.catalog_product_id` links placements to it.
 * The resolver's identity precedence (ADR-37 §2.1): the placement
 * (`menu_items`) wins when it carries a value, else this master row, else
 * defaults. Deliberately identity-only for now — price/modifier/stock land in
 * later slices (price book = slice 5, modifier groups = slice 2).
 */
export interface CatalogProduct {
  id: string;
  business_id: string;
  /** Canonical display name — the kitchen_name fallback and future master name. */
  title: string;
  /** Stable POS/SKU identifier, unique per business. Null = no PLU yet. */
  plu?: string | null;
  /** Optional barcode/EAN/UPC for scanner entry at the POS. */
  barcode?: string | null;
  /** Kitchen/bar display name printed on tickets and KDS. */
  kitchen_name?: string | null;
  /** POS visibility, independent of customer-menu visibility. Default true. */
  pos_visible?: boolean;
  /** How the POS treats the line when adding it to an order. Default `normal`. */
  product_behavior?: MenuItemProductBehavior;
  created_at?: string;
  updated_at?: string;
}

export type MenuItemImageSourceName = 'pixabay' | 'pexels' | 'unsplash';

export interface MenuItemImageCandidate {
  id: string;
  url: string;
  thumbnail: string;
  source: MenuItemImageSourceName;
  license?: string | null;
  licenseUrl?: string | null;
  creator?: string | null;
  creatorUrl?: string | null;
  pageUrl?: string | null;
  score?: number;
  reason?: string | null;
}

export interface MenuItemImageSource {
  id: string;
  menu_item_id: string;
  url: string;
  bucket_path?: string | null;
  source: MenuItemImageSourceName;
  source_id?: string | null;
  license?: string | null;
  license_url?: string | null;
  creator?: string | null;
  creator_url?: string | null;
  page_url?: string | null;
  rank_score?: number | null;
  created_at?: string;
}

export interface BranchMenuItemServiceOverride {
  id: string;
  branch_id: string;
  menu_item_id: string;
  sell_state: MenuItemSellState;
  reason?: string | null;
  stop_sell_until?: string | null;
  notes?: string | null;
  price_override?: number | null;
  // Layered branch sharing (doc 38 Part 2). `false` = this branch does not carry
  // the (shared/base) item at all , structurally excluded, distinct from a
  // temporary sold_out/paused. Defaults true, so absence means "carried".
  is_carried?: boolean;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export const fonts = [
  { name: 'Abril Fatface', css: 'font-abril' },
  { name: 'Alex Brush', css: 'font-alex-brush' },
  { name: 'Allura', css: 'font-allura' },
  { name: 'Amatic SC', css: 'font-amatic' },
  { name: 'Anton', css: 'font-anton' },
  { name: 'Archivo', css: 'font-archivo' },
  { name: 'Arizonia', css: 'font-arizonia' },
  { name: 'Arvo', css: 'font-arvo' },
  { name: 'Bad Script', css: 'font-bad-script' },
  { name: 'Barlow', css: 'font-barlow' },
  { name: 'Bebas Neue', css: 'font-bebas' },
  { name: 'Bilbo Swash Caps', css: 'font-bilbo-swash-caps' },
  { name: 'Cabin', css: 'font-cabin' },
  { name: 'Caveat', css: 'font-caveat' },
  { name: 'Chewy', css: 'font-chewy' },
  { name: 'Cinzel', css: 'font-cinzel' },
  { name: 'Clicker Script', css: 'font-clicker-script' },
  { name: 'Condiment', css: 'font-condiment' },
  { name: 'Cormorant Garamond', css: 'font-cormorant' },
  { name: 'Cookie', css: 'font-cookie' },
  { name: 'Crimson Text', css: 'font-crimson' },
  { name: 'Dancing Script', css: 'font-dancing' },
  { name: 'DM Sans', css: 'font-dm-sans' },
  { name: 'Euphoria Script', css: 'font-euphoria-script' },
  { name: 'Exo 2', css: 'font-exo2' },
  { name: 'Fira Code', css: 'font-fira-code' },
  { name: 'Fira Sans', css: 'font-fira' },
  { name: 'Fondamento', css: 'font-fondamento' },
  { name: 'Fredoka', css: 'font-fredoka' },
  { name: 'Fugaz One', css: 'font-fugaz-one' },
  { name: 'Geist', css: 'font-geist-sans' },
  { name: 'Geist Mono', css: 'font-geist-mono' },
  { name: 'Gloria Hallelujah', css: 'font-gloria-hallelujah' },
  { name: 'Grand Hotel', css: 'font-grand-hotel' },
  { name: 'Gravitas One', css: 'font-gravitas' },
  { name: 'Great Vibes', css: 'font-great-vibes' },
  { name: 'Herr Von Muellerhoff', css: 'font-herr-von-muellerhoff' },
  { name: 'Inconsolata', css: 'font-inconsolata' },
  { name: 'Indie Flower', css: 'font-indie' },
  { name: 'Inter', css: 'font-inter' },
  { name: 'Italianno', css: 'font-italianno' },
  { name: 'JetBrains Mono', css: 'font-jetbrains' },
  { name: 'Josefin Sans', css: 'font-josefin' },
  { name: 'Kaushan Script', css: 'font-kaushan-script' },
  { name: 'Karla', css: 'font-karla' },
  { name: 'Lato', css: 'font-lato' },
  { name: 'Lexend', css: 'font-lexend' },
  { name: 'Lobster', css: 'font-lobster' },
  { name: 'Lora', css: 'font-lora' },
  { name: 'Macondo', css: 'font-macondo' },
  { name: 'Manrope', css: 'font-manrope' },
  { name: 'Marck Script', css: 'font-marck-script' },
  { name: 'Merriweather', css: 'font-merriweather' },
  { name: 'Monoton', css: 'font-monoton' },
  { name: 'Montserrat', css: 'font-montserrat' },
  { name: 'Mr Bedfort', css: 'font-mr-bedfort' },
  { name: 'Mr Dafoe', css: 'font-mr-dafoe' },
  { name: 'Mulish', css: 'font-mulish' },
  { name: 'Norican', css: 'font-norican' },
  { name: 'Noto Serif', css: 'font-noto-serif' },
  { name: 'Nunito', css: 'font-nunito' },
  { name: 'Open Sans', css: 'font-open-sans' },
  { name: 'Oswald', css: 'font-oswald' },
  { name: 'Pacifico', css: 'font-pacifico' },
  { name: 'Parisienne', css: 'font-parisienne' },
  { name: 'Permanent Marker', css: 'font-permanent-marker' },
  { name: 'Playfair Display', css: 'font-playfair' },
  { name: 'Poppins', css: 'font-poppins' },
  { name: 'Prompt', css: 'font-prompt' },
  { name: 'PT Sans', css: 'font-pt-sans' },
  { name: 'Quicksand', css: 'font-quicksand' },
  { name: 'Qwigley', css: 'font-qwigley' },
  { name: 'Raleway', css: 'font-raleway' },
  { name: 'Righteous', css: 'font-righteous' },
  { name: 'Rock Salt', css: 'font-rock-salt' },
  { name: 'Roboto', css: 'font-roboto' },
  { name: 'Roboto Slab', css: 'font-roboto-slab' },
  { name: 'Rouge Script', css: 'font-rouge-script' },
  { name: 'Rubik', css: 'font-rubik' },
  { name: 'Rye', css: 'font-rye' },
  { name: 'Sacramento', css: 'font-sacramento' },
  { name: 'Sans', css: 'font-sans'},
  { name: 'Satisfy', css: 'font-satisfy' },
  { name: 'Shadows Into Light', css: 'font- ' },
  { name: 'Shadows Into Light Two', css: 'font-shadows-two' },
  { name: 'Sora', css: 'font-sora' },
  { name: 'Source Sans 3', css: 'font-source-sans' },
  { name: 'Space Grotesk', css: 'font-space-grotesk' },
  { name: 'Unica One', css: 'font-unica-one' },
  { name: 'UnifrakturCook', css: 'font-unifraktur-cook' },
  { name: 'UnifrakturMaguntia', css: 'font-unifraktur-maguntia' },
  { name: 'Ubuntu', css: 'font-ubuntu' },
  { name: 'Varela Round', css: 'font-varela' },
  { name: 'Vast Shadow', css: 'font-vast-shadow' },
  { name: 'Work Sans', css: 'font-work-sans' },
  { name: 'Yellowtail', css: 'font-yellowtail' },
  { name: 'Yeseva One', css: 'font-yeseva-one' },
  { name: 'Zilla Slab', css: 'font-zilla-slab' },
] as const;

export const currencies = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£' },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'Mex$' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'ر.س' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'PLN', name: 'Polish Złoty', symbol: 'zł' },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
  { code: 'COP', name: 'Colombian Peso', symbol: 'COL$' },
  { code: 'ARS', name: 'Argentine Peso', symbol: 'AR$' },
  { code: 'CLP', name: 'Chilean Peso', symbol: 'CLP$' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'TWD', name: 'Taiwan Dollar', symbol: 'NT$' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.' },
  { code: 'DZD', name: 'Algerian Dinar', symbol: 'دج' },
  { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br' },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh' },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh' },
  { code: 'SDG', name: 'Sudanese Pound', symbol: '£' },
  { code: 'XOF', name: 'West African CFA franc', symbol: 'CFA' },
  { code: 'XAF', name: 'Central African CFA franc', symbol: 'FCFA' },
  // ...add more as needed
] as const

// Menu categories
export const menuCategories = [
  'Appetizers',
  'Salads',
  'Soups',
  'Main Course',
  'Pasta',
  'Pizza',
  'Burgers',
  'Sandwiches',
  'Seafood',
  'Steaks',
  'Chicken',
  'Vegetarian',
  'Vegan',
  'Desserts',
  'Beverages',
  'Coffee',
  'Tea',
  'Cocktails',
  'Wine',
  'Beer',
] as const;

export const ALLERGENS: readonly string[] = ALLERGEN_LABELS;

export const allergens = ALLERGENS;

export const DIETARY_TAGS: readonly string[] = DIETARY_TAG_ENTRIES.map((e) => e.label);

export interface MenuFormData {
  title: string;
  description?: string;
  slug: string;
  branch_id?: string;
  content_menu_id?: string;
  template_id?: string;
  theme_id?: string;
}

export interface MenuItemFormData {
  name: string;
  description?: string;
  price: number;
  category_id: string;
  image?: File;
  variants?: { [key: string]: string };
  available_Days?: string[]; // e.g. ['Monday', 'Wednesday']
  available_Times?: { start: string; end: string }; // e.g. { start: '11:00', end: '14:00' }
  available: boolean;
  is_featured?: boolean;
  is_coming_soon?: boolean;
  tags?: string[];
}

export interface MenuItemAvailabilityRule {
  id: string;
  menu_item_id: string;
  timezone: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface MenuItemAvailabilityException {
  id: string;
  menu_item_id: string;
  exception_date: string;
  start_time?: string;
  end_time?: string;
  is_available: boolean;
  reason?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * ADR-37 slice 4: recurring availability window for a LINE (brands) or a
 * specific MENU at a branch, optionally scoped to one service channel.
 * NULL channel = applies to all channels. end_time < start_time wraps past
 * midnight. lead_minutes stops ordering before end_time (kitchen lead).
 */
export interface MenuScheduleWindow {
  id: string;
  line_id?: string | null;
  menu_id?: string | null;
  branch_id: string;
  channel?: string | null;
  timezone: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  lead_minutes?: number | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * ADR-37 slice 4: one-off override for a line/menu at a branch on a specific
 * date. is_available=false with a window = temporarily closed during it;
 * is_available=false with no window = closed all day; is_available=true forces
 * availability.
 */
export interface MenuScheduleException {
  id: string;
  line_id?: string | null;
  menu_id?: string | null;
  branch_id: string;
  channel?: string | null;
  exception_date: string;
  start_time?: string | null;
  end_time?: string | null;
  is_available: boolean;
  reason?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface MenuItemOptionGroup {
  id: string;
  menu_item_id: string;
  name: string;
  min_select?: number;
  max_select?: number;
  required?: boolean;
  // ADR-37 slice 2 (P2-1): reuseable groups may lift the max-select cap. The
  // order sweeps and validateOptionGroups treat `unlimited: true` as no
  // maximum; absent/false keeps the declared max_select.
  unlimited?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

export interface MenuItemOption {
  id: string;
  option_group_id: string;
  name: string;
  price_delta?: number;
  is_default?: boolean;
  external_code?: string;
  display_order?: number;
  image?: string | null;
  /** INV-03: when set, selecting this option consumes the linked item's recipe. */
  recipe_menu_item_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

